import { Controller } from "@hotwired/stimulus"
import { createLineChart, destroyChart } from "../modules/money_smart/chart_helpers"
import { debtPayoffDatasets, renderStrategyComparison, renderPayoffSchedule, renderDebtPayoffInsights } from "../modules/money_smart/debt_payoff_helpers"

const FREQUENCY_MULTIPLIERS = { monthly: 1, fortnightly: 26 / 12, weekly: 52 / 12 }
const FREQUENCY_LABELS = { monthly: "Monthly", fortnightly: "Fortnightly", weekly: "Weekly" }

export default class extends Controller {
  static targets = [
    "form", "extraPayment", "frequency", "submitButton",
    "results", "errors", "errorMessage", "chart", "chartCard", "scheduleCard",
    "comparisonContainer", "snowballScheduleBody", "avalancheScheduleBody",
    "scheduleTabSnowball", "scheduleTabAvalanche",
    "snowballSchedulePanel", "avalancheSchedulePanel", "insightsContainer",
    "printButton"
  ]

  static values = { calculateUrl: String, autoCalculate: Boolean }

  chart = null

  connect() {
    this.prefillFromSharedData() || this.prefillFromUrl()
  }

  disconnect() {
    destroyChart(this.chart)
  }

  get frequencyMultiplier() {
    const freq = this.hasFrequencyTarget ? this.frequencyTarget.value : "monthly"
    return FREQUENCY_MULTIPLIERS[freq] || 1
  }

  toMonthly(value) {
    return (parseFloat(value) || 0) * this.frequencyMultiplier
  }

  updateFrequencyLabels() {
    const freq = this.hasFrequencyTarget ? this.frequencyTarget.value : "monthly"
    const label = FREQUENCY_LABELS[freq] || "Monthly"

    this.element.querySelectorAll("[data-frequency-label='min_payment']").forEach(el => {
      el.textContent = `Min ${label} Payment ($)`
    })

    this.element.querySelectorAll("[data-frequency-label='extra_payment']").forEach(el => {
      el.innerHTML = `Extra ${label} Payment ($) <span class="text-stone-400 font-normal">— on top of minimums</span>`
    })
  }

  validateDebtItem(event) {
    const item = event.target.closest("[data-dynamic-list-target='item']")
    if (item) this.enforceMinForItem(item)
  }

  enforceMinimumPayments() {
    const items = this.element.querySelectorAll("[data-dynamic-list-target='item']")
    items.forEach(item => this.enforceMinForItem(item))
  }

  enforceMinForItem(item) {
    const multiplier = this.frequencyMultiplier
    const balInput = item.querySelector("[data-field='balance']")
    const rateInput = item.querySelector("[data-field='rate']")
    const minInput = item.querySelector("[data-field='min_payment']")
    if (!balInput || !rateInput || !minInput) return

    const balance = parseFloat(balInput.value) || 0
    const rate = parseFloat(rateInput.value) || 0
    const monthlyInterest = balance * rate / 100 / 12
    const enteredMonthly = (parseFloat(minInput.value) || 0) * multiplier

    let existing = item.querySelector("[data-min-warning]")

    if (monthlyInterest > 0 && enteredMonthly < monthlyInterest) {
      const corrected = Math.ceil(monthlyInterest / multiplier)
      minInput.value = corrected
      minInput.classList.add("!border-red-400", "!ring-1", "!ring-red-300")

      if (!existing) {
        existing = document.createElement("p")
        existing.setAttribute("data-min-warning", "")
        existing.className = "text-xs text-red-500 mt-0.5"
        minInput.parentElement.appendChild(existing)
      }
      existing.textContent = `Raised to cover interest ($${monthlyInterest.toFixed(0)}/mo)`
      existing.classList.remove("hidden")
    } else {
      minInput.classList.remove("!border-red-400", "!ring-1", "!ring-red-300")
      if (existing) existing.classList.add("hidden")
    }
  }

  collectDebts() {
    const items = this.element.querySelectorAll("[data-dynamic-list-target='item']")
    return Array.from(items).map(item => ({
      name: item.querySelector("[data-field='name']")?.value || "",
      balance: item.querySelector("[data-field='balance']")?.value || "0",
      rate: item.querySelector("[data-field='rate']")?.value || "0",
      min_payment: item.querySelector("[data-field='min_payment']")?.value || "0"
    }))
  }

  async calculate(event) {
    event.preventDefault()

    this.enforceMinimumPayments()

    const rawDebts = this.collectDebts()
    const rawExtra = this.extraPaymentTarget.value || "0"

    const debts = rawDebts.map(d => ({
      ...d,
      min_payment: String(this.toMonthly(d.min_payment))
    }))
    const extra = String(this.toMonthly(rawExtra))

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = "Calculating..."
    this.resultsTarget.classList.add("hidden")
    this.errorsTarget.classList.add("hidden")

    try {
      const response = await fetch(this.calculateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken },
        body: JSON.stringify({ debts, extra_payment: extra })
      })

      const data = await response.json()

      if (response.ok) {
        this.renderResults(data)
        this.updateUrl(rawDebts, rawExtra)
      } else {
        this.errorMessageTarget.textContent = data.errors?.join(", ") || "Calculation failed"
        this.errorsTarget.classList.remove("hidden")
      }
    } catch {
      this.errorMessageTarget.textContent = "Something went wrong. Please try again."
      this.errorsTarget.classList.remove("hidden")
    } finally {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = "Compare Strategies"
    }
  }

  async renderResults(data) {
    const failed = data.comparison?.strategies_failed

    renderStrategyComparison(this.comparisonContainerTarget, data)
    renderDebtPayoffInsights(this.insightsContainerTarget, data.insights)

    destroyChart(this.chart)

    if (failed) {
      this.chartCardTarget.classList.add("hidden")
      this.scheduleCardTarget.classList.add("hidden")
    } else {
      this.chartCardTarget.classList.remove("hidden")
      this.scheduleCardTarget.classList.remove("hidden")

      renderPayoffSchedule(this.snowballScheduleBodyTarget, data.snowball_schedule)
      renderPayoffSchedule(this.avalancheScheduleBodyTarget, data.avalanche_schedule)
      this.switchToSnowball()

      const datasets = debtPayoffDatasets(data.chart_data)
      this.chart = await createLineChart(this.chartTarget, data.chart_data.labels, datasets)
    }

    this.resultsTarget.classList.remove("hidden")
  }

  switchToSnowball() {
    this.scheduleTabSnowballTarget.classList.add("text-violet-700", "font-semibold", "border-b-2", "border-violet-700")
    this.scheduleTabSnowballTarget.classList.remove("text-stone-400")
    this.scheduleTabAvalancheTarget.classList.remove("text-violet-700", "font-semibold", "border-b-2", "border-violet-700")
    this.scheduleTabAvalancheTarget.classList.add("text-stone-400")
    this.snowballSchedulePanelTarget.classList.remove("hidden")
    this.avalancheSchedulePanelTarget.classList.add("hidden")
  }

  switchToAvalanche() {
    this.scheduleTabAvalancheTarget.classList.add("text-violet-700", "font-semibold", "border-b-2", "border-violet-700")
    this.scheduleTabAvalancheTarget.classList.remove("text-stone-400")
    this.scheduleTabSnowballTarget.classList.remove("text-violet-700", "font-semibold", "border-b-2", "border-violet-700")
    this.scheduleTabSnowballTarget.classList.add("text-stone-400")
    this.avalancheSchedulePanelTarget.classList.remove("hidden")
    this.snowballSchedulePanelTarget.classList.add("hidden")
  }

  updateUrl(debts, extra) {
    const params = new URLSearchParams()
    const freq = this.hasFrequencyTarget ? this.frequencyTarget.value : "monthly"
    debts.forEach((d, i) => {
      params.set(`d${i}_name`, d.name)
      params.set(`d${i}_bal`, d.balance)
      params.set(`d${i}_rate`, d.rate)
      params.set(`d${i}_min`, d.min_payment)
    })
    params.set("extra_payment", extra)
    params.set("freq", freq)
    history.replaceState(null, "", `${window.location.pathname}?${params}`)
  }

  prefillFromSharedData() {
    const scriptEl = document.querySelector("script[data-moneysmart-prefill]")
    if (!scriptEl) return false

    let data
    try { data = JSON.parse(scriptEl.textContent) } catch { return false }

    const debts = data.debts
    if (!Array.isArray(debts) || debts.length === 0) return false

    const container = this.element.querySelector("[data-dynamic-list-target='container']")
    const template = this.element.querySelector("#debt-item-template")
    if (!container || !template) return false

    container.querySelectorAll("[data-dynamic-list-target='item']").forEach(item => item.remove())

    debts.forEach(d => {
      const clone = template.content.firstElementChild.cloneNode(true)
      const nameInput = clone.querySelector("[data-field='name']")
      const balInput = clone.querySelector("[data-field='balance']")
      const rateInput = clone.querySelector("[data-field='rate']")
      const minInput = clone.querySelector("[data-field='min_payment']")

      if (nameInput) nameInput.value = d.name || ""
      if (balInput) balInput.value = d.balance || ""
      if (rateInput) rateInput.value = d.rate || ""
      if (minInput) minInput.value = d.min_payment || ""

      container.appendChild(clone)
    })

    if (data.extra_payment) {
      this.extraPaymentTarget.value = data.extra_payment
    }

    this.formTarget.requestSubmit()
    return true
  }

  prefillFromUrl() {
    const params = new URLSearchParams(window.location.search)
    if (!params.has("d0_bal")) return

    const container = this.element.querySelector("[data-dynamic-list-target='container']")
    const template = this.element.querySelector("#debt-item-template")
    if (!container || !template) return

    container.querySelectorAll("[data-dynamic-list-target='item']").forEach(item => item.remove())

    let i = 0
    while (params.has(`d${i}_bal`)) {
      const clone = template.content.firstElementChild.cloneNode(true)

      const nameInput = clone.querySelector("[data-field='name']")
      const balInput = clone.querySelector("[data-field='balance']")
      const rateInput = clone.querySelector("[data-field='rate']")
      const minInput = clone.querySelector("[data-field='min_payment']")

      if (nameInput) nameInput.value = params.get(`d${i}_name`) || ""
      if (balInput) balInput.value = params.get(`d${i}_bal`) || ""
      if (rateInput) rateInput.value = params.get(`d${i}_rate`) || ""
      if (minInput) minInput.value = params.get(`d${i}_min`) || ""

      container.appendChild(clone)
      i++
    }

    if (params.has("extra_payment")) {
      this.extraPaymentTarget.value = params.get("extra_payment")
    }

    if (params.has("freq") && this.hasFrequencyTarget) {
      this.frequencyTarget.value = params.get("freq")
      this.updateFrequencyLabels()
    }

    this.formTarget.requestSubmit()
  }

  print() {
    window.print()
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
